/**
 * GA4 Event Tracking (Client-Side)
 *
 * Handles interaction-based events via dataLayer pushes:
 * - add_to_cart: when product is added via AJAX
 * - cta_click: when key call-to-action buttons are clicked
 * - generate_lead: newsletter signup (triggered from Alpine in footer)
 * - search: site search queries
 * - article_scroll: scroll depth on educational content pages
 * - video_start / video_progress / video_complete: YouTube embeds
 */

/**
 * Push an event to dataLayer.
 * Clears ecommerce object first if the event contains ecommerce data.
 */
function pushEvent(eventName, params = {}) {
  window.dataLayer = window.dataLayer || [];
  if (params.ecommerce) {
    window.dataLayer.push({ ecommerce: null });
  }
  window.dataLayer.push({ event: eventName, ...params });
}

/**
 * Track add_to_cart via WooCommerce's jQuery added_to_cart event.
 * Uses product data from window.dhTrackingData (output by app/tracking.php).
 */
function initAddToCartTracking() {
  if (!window.jQuery) return;

  jQuery(document.body).on('added_to_cart', function (event, fragments, cart_hash, $button) {
    const button = $button?.[0] || $button;
    if (!button) return;

    const productId = button.dataset?.product_id;
    const trackingData = window.dhTrackingData;
    const quantity = parseInt(button.dataset?.quantity) || 1;

    if (trackingData?.products?.[productId]) {
      const item = { ...trackingData.products[productId], quantity };
      pushEvent('add_to_cart', {
        ecommerce: {
          currency: trackingData.currency || 'USD',
          value: item.price * quantity,
          items: [item],
        },
      });
    } else {
      // Fallback when product data isn't pre-loaded (e.g. mini cart add)
      pushEvent('add_to_cart', {
        ecommerce: {
          currency: 'USD',
          value: 0,
          items: [{
            item_id: productId || 'unknown',
            item_name: button.dataset?.product_name || 'Product',
            quantity: quantity,
          }],
        },
      });
    }
  });
}

/**
 * Track clicks on key CTA buttons/links.
 *
 * Auto-tracks:
 * - Elements with [data-track-cta] attribute
 * - Links inside .cta-section or .hero sections that go to /shop or /product
 * - Smart purchase bar add-to-cart buttons
 *
 * Uses event delegation for efficiency and dynamic elements.
 */
function initCTATracking() {
  document.addEventListener('click', function (e) {
    // Explicit opt-in via data attribute
    const tracked = e.target.closest('[data-track-cta]');
    if (tracked) {
      pushEvent('cta_click', {
        cta_text: (tracked.dataset.trackCta || tracked.textContent?.trim()).substring(0, 60),
        cta_url: tracked.href || '',
        cta_location: tracked.dataset.ctaLocation || getSectionName(tracked),
        page_path: window.location.pathname,
      });
      return;
    }

    // Auto-track: links to shop/product pages from CTA sections, hero, or homepage sections
    const link = e.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    const isConversionLink = /\/(shop|product|checkout|cart)\b/.test(href);
    if (!isConversionLink) return;

    const section = link.closest(
      '.cta-section, [class*="hero"], [class*="cta-block"], .smart-purchase-bar, ' +
      'section[class*="homepage"]'
    );
    if (!section) return;

    pushEvent('cta_click', {
      cta_text: link.textContent?.trim().substring(0, 60),
      cta_url: href,
      cta_location: getSectionName(link),
      page_path: window.location.pathname,
    });
  });
}

/**
 * Get a readable section name from the nearest section/container.
 */
function getSectionName(el) {
  const section = el.closest('section, [class*="section"], .smart-purchase-bar');
  if (!section) return 'page';

  // Try to find a meaningful class name
  const classes = Array.from(section.classList);
  const meaningful = classes.find(c =>
    /hero|cta|banner|feature|benefit|product|shop|purchase/i.test(c)
  );
  return meaningful || classes[0] || 'section';
}

/**
 * Track site search queries from the URL parameter.
 */
function initSearchTracking() {
  const searchTerm = new URLSearchParams(window.location.search).get('s');
  if (searchTerm) {
    pushEvent('search', { search_term: searchTerm });
  }
}

/**
 * Track scroll depth on educational content pages.
 * Fires at 50% and 90% (effectively "read to end") scroll depth.
 */
function initContentScrollTracking() {
  const contentTypes = ['resource', 'wellness', 'benefit', 'support', 'faq'];
  const body = document.body;

  const isContentPage = contentTypes.some(type =>
    body.classList.contains(`single-${type}`)
  );
  if (!isContentPage) return;

  let hit50 = false;
  let hit90 = false;

  window.addEventListener('scroll', function () {
    const percent = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100;

    if (!hit50 && percent >= 50) {
      hit50 = true;
      pushEvent('article_scroll', {
        scroll_depth: '50',
        page_path: window.location.pathname,
        page_title: document.title,
      });
    }

    if (!hit90 && percent >= 90) {
      hit90 = true;
      pushEvent('article_scroll', {
        scroll_depth: '100',
        page_path: window.location.pathname,
        page_title: document.title,
      });
    }
  }, { passive: true });
}

/**
 * Track YouTube embed engagement (video_start, video_progress, video_complete).
 *
 * GA4 enhanced measurement can't track youtube-nocookie.com embeds, so we use
 * the YouTube iframe API directly and mirror GA4's standard video event names
 * and parameters. Requires embeds to include enablejsapi=1 in their src.
 */
function initVideoTracking() {
  const iframes = document.querySelectorAll(
    'iframe[src*="youtube-nocookie.com/embed/"], iframe[src*="youtube.com/embed/"]'
  );
  const trackable = Array.from(iframes).filter(f => f.src.includes('enablejsapi=1'));
  if (!trackable.length) return;

  // Load the YouTube iframe API once, preserving any existing ready callback
  if (!window.YT?.Player) {
    const existing = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      existing?.();
      trackable.forEach(attachVideoPlayer);
    };
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  } else {
    trackable.forEach(attachVideoPlayer);
  }
}

/**
 * Defer until the iframe has loaded the YouTube document. With loading="lazy",
 * YT.Player must not attach while contentWindow is still about:blank or the
 * postMessage handshake binds to a stale window and never completes.
 */
function attachVideoPlayer(iframe) {
  const youtubeDocLoaded = () => {
    try {
      void iframe.contentWindow.location.href; // accessible = still about:blank
      return false;
    } catch {
      return true; // cross-origin = YouTube document is in
    }
  };

  if (youtubeDocLoaded()) {
    createVideoPlayer(iframe);
    return;
  }

  // loading="lazy" iframe not loaded yet. The initial about:blank can also
  // fire a load event, so re-check the origin on each one.
  iframe.addEventListener('load', function onLoad() {
    if (!youtubeDocLoaded()) return;
    iframe.removeEventListener('load', onLoad);
    createVideoPlayer(iframe);
  });
}

function createVideoPlayer(iframe) {
  let started = false;
  const firedThresholds = new Set();
  let progressTimer = null;

  // With nocookie embeds the API's convenience methods (getDuration etc.)
  // often never install, but playerInfo is always populated via infoDelivery.
  // Prefer the method, fall back to playerInfo, never throw.
  const read = (player, method, infoKey) => {
    try {
      if (typeof player[method] === 'function') return player[method]();
    } catch { /* fall through */ }
    return player.playerInfo?.[infoKey];
  };

  const player = new YT.Player(iframe, {
    events: {
      onStateChange(event) {
        const params = () => {
          const duration = read(player, 'getDuration', 'duration') || 0;
          const currentTime = read(player, 'getCurrentTime', 'currentTime') || 0;
          return {
            video_provider: 'youtube',
            video_title: player.playerInfo?.videoData?.title || iframe.title || '',
            video_url: read(player, 'getVideoUrl', 'videoUrl') || iframe.src,
            video_duration: Math.round(duration),
            video_current_time: Math.round(currentTime),
            video_percent: duration ? Math.round((currentTime / duration) * 100) : 0,
            page_path: window.location.pathname,
          };
        };

        if (event.data === YT.PlayerState.PLAYING) {
          if (!started) {
            started = true;
            pushEvent('video_start', params());
          }
          // Poll while playing to catch progress milestones
          clearInterval(progressTimer);
          progressTimer = setInterval(() => {
            const duration = read(player, 'getDuration', 'duration');
            if (!duration) return;
            const percent = ((read(player, 'getCurrentTime', 'currentTime') || 0) / duration) * 100;
            [10, 25, 50, 75].forEach(threshold => {
              if (percent >= threshold && !firedThresholds.has(threshold)) {
                firedThresholds.add(threshold);
                pushEvent('video_progress', { ...params(), video_percent: threshold });
              }
            });
          }, 1000);
        } else {
          clearInterval(progressTimer);
          if (event.data === YT.PlayerState.ENDED && !firedThresholds.has('complete')) {
            firedThresholds.add('complete');
            pushEvent('video_complete', { ...params(), video_percent: 100 });
          }
        }
      },
    },
  });
}

/**
 * Expose a global tracking function for use in Blade/Alpine templates.
 * Usage: window.dhTrack('event_name', { key: 'value' })
 */
window.dhTrack = pushEvent;

/**
 * Initialize all client-side tracking.
 */
export function initTracking() {
  initAddToCartTracking();
  initCTATracking();
  initSearchTracking();
  initContentScrollTracking();
  initVideoTracking();
}
